import React, { useMemo } from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import {
  alpha,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { RHFMultiSelect, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { IUnifiedFilter } from 'src/contexts/SearchFilterContext';

// 타입 전용 import (런타임 순환 참조 방지: SearchFilter -> KeywordPortal -> FilterSection)
import type { FilterSectionKey } from './SearchFilter';

interface FilterSectionProps {
  section: FilterSectionKey;
  color: string;
  control: Control<IUnifiedFilter, any>;
  watch: UseFormWatch<IUnifiedFilter>;
  codeStore: any;
  pathname: string;
  onSubmit?: () => void;
}

// 진행중 여부 옵션 (둘 다 선택 시 = 전체 => 파라미터 미전송)
export const PROG_YN_OPTIONS: { label: string; value: string }[] = [
  { label: '진행중', value: 'true' },
  { label: '종료', value: 'false' },
];

// 진행중 여부: 전체 선택 상태 (둘 다 선택 => API로 progYn 미전송)
export const PROG_YN_ALL = PROG_YN_OPTIONS.map((v) => v.value);

/**
 * 필터 한 줄(라벨 + 입력) 공통 레이아웃
 * 세로 스크롤 최소화를 위해 라벨은 caption, 항목 간 간격은 좁게 통일
 */
export const FilterRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack gap={0.5}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {children}
  </Stack>
);

/**
 * 토글 버튼 그룹 공통 스타일 (신규/기저, 스팟/지속, 진행중 여부 동일 적용)
 * 선택 상태는 진한 배경 대신 옅은 틴트 + 컬러 텍스트로 표현해
 * "전체 선택(=필터 미적용)"이 과하게 강조되지 않도록 한다.
 */
const toggleGroupSx = (color: string) => ({
  // 그룹 내부 여백 (theme의 grouped margin:4 와 동일한 간격을 유지)
  gap: 0.5,
  p: 0.5,
  /**
   * MUI ToggleButtonGroup 은 버튼들이 테두리를 공유하도록
   *  - 첫 버튼 외: marginLeft:-1, borderLeft: transparent, 왼쪽 radius 0
   *  - 마지막 버튼 외: 오른쪽 radius 0
   *  - 선택 + 선택 인접: borderLeft: 0
   * 규칙을 .MuiToggleButtonGroup-grouped 클래스에 건다.
   * 여기서는 버튼을 각각 독립된 박스로 보여주므로 위 규칙을 모두 되돌린다.
   * (동일 클래스로 지정해야 우선순위가 밀리지 않는다)
   */
  '& .MuiToggleButtonGroup-grouped': {
    width: '100%',
    py: 0.25,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    textTransform: 'none' as const,
    color: 'text.secondary',
    margin: '0 !important',
    borderRadius: '4px !important',
    // 미선택 상태도 구분되도록 4면 테두리를 항상 유지
    // (theme override / MUI 기본 규칙보다 뒤에 주입되지 않으므로 !important 필요)
    border: `1px solid ${alpha(color, 0.24)} !important`,
    '&:not(:last-of-type)': {
      borderTopRightRadius: '4px !important',
      borderBottomRightRadius: '4px !important',
    },
    '&.Mui-selected': {
      backgroundColor: `${alpha(color, 0.14)} !important`,
      color: `${color} !important`,
      fontWeight: 600,
      border: `1px solid ${alpha(color, 0.6)} !important`,
      '&:hover': {
        backgroundColor: `${alpha(color, 0.22)} !important`,
      },
    },
  },
});

// 진행중 여부 + 키워드 검색 섹션 - SearchFilter 내부용
const KeywordSection: React.FC<{ control: Control<IUnifiedFilter, any>; color: string }> = ({
  control,
  color,
}) => {
  return (
    <>
      {/* 진행중 여부 */}
      <FilterRow label="진행중 여부">
        <Controller
          name="progYnList"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              {...field}
              size="small"
              value={field.value ?? []}
              onChange={(_, v) => {
                if (v.length === 0) return;
                field.onChange(v);
              }}
              aria-label="progYnList"
              sx={toggleGroupSx(color)}
            >
              {PROG_YN_OPTIONS.map((v, i) => (
                <ToggleButton key={'PROG_YN-' + i} value={v.value}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </FilterRow>

      {/* 키워드 */}
      <FilterRow label="키워드">
        <RHFTextField
          name="text"
          label=""
          placeholder="검색어를 입력하세요"
          helperText="[검색 항목]: 프로젝트명, 고객사, 계약사 , ESM 코드 , UUID , 사업관리코드 , 사업기안번호"
          size="small"
          fullWidth
          FormHelperTextProps={{ sx: { mt: 0.25, fontSize: '0.6875rem', lineHeight: 1.4 } }}
        />
      </FilterRow>
    </>
  );
};

export const FilterSection: React.FC<FilterSectionProps> = ({
  section,
  color,
  control,
  watch,
  codeStore,
  pathname,
  onSubmit,
}) => {
  const lostCode = 200307;
  const codeData = useMemo<{
    NEW_STATUS: { label: string; value: number | string }[];
    ONGOING: { label: string; value: number | string }[];
    SIGN: { label: string; value: number }[];
    BZ_STEP: { label: string; value: number | string }[];
  }>(
    () => ({
      NEW_STATUS: codeStore.getCodeByName('NEW_STATUS_CD') ?? [],
      ONGOING: codeStore.getCodeByName('ONGOING_CD') ?? [],
      SIGN: codeStore.getCodeByName('SIGN_CD') ?? [],
      BZ_STEP:
        codeStore.getCodeByName('BZ_STEP_CD').filter(
          // "실주" 제외
          (v: { value: number | string; label: string }) => String(v.value) !== String(lostCode),
        ) ?? [],
    }),
    [codeStore],
  );

  const prefix = section;
  const isProjectPage = section === 'common';
  const capital = isProjectPage
    ? pathname.slice(1).charAt(0).toUpperCase() + pathname.slice(2)
    : prefix.charAt(0).toUpperCase() + prefix.slice(1);

  // 진행중 여부 + 키워드
  if (section === 'keyword') {
    return <KeywordSection control={control} color={color} />;
  } else {
    return (
      <Stack gap={1.5}>
        {!isProjectPage && <Typography variant="subtitle1">{capital}</Typography>}

        {/* 신규/기저 */}
        <FilterRow label="신규/기저">
          <Controller
            name={
              isProjectPage
                ? 'newStatusCdList'
                : (`${prefix}NewStatusCdList` as keyof IUnifiedFilter)
            }
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                size="small"
                value={field.value ?? []}
                onChange={(_, v) => {
                  if (v.length === 0) return;
                  field.onChange(v);
                }}
                aria-label={`${prefix}NewStatusCdList`}
                sx={toggleGroupSx(color)}
              >
                {codeData.NEW_STATUS.map((v, i) => (
                  <ToggleButton key={'NEW_STATUS_CD-' + i} value={String(v.value)}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </FilterRow>

        {/* 스팟/지속 */}
        <FilterRow label="스팟/지속">
          <Controller
            name={
              isProjectPage ? 'ongoingCdList' : (`${prefix}OngoingCdList` as keyof IUnifiedFilter)
            }
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                size="small"
                value={field.value ?? []}
                onChange={(_, v) => {
                  if (v.length === 0) return;
                  field.onChange(v);
                }}
                sx={toggleGroupSx(color)}
              >
                {codeData.ONGOING.map((v, i) => (
                  <ToggleButton key={`ONGOING_CD-${i}`} value={String(v.value)}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </FilterRow>

        {/* 영업단계 */}
        <FilterRow label="영업단계">
          <Stack direction="row" gap={1} alignItems="center">
            <Controller
              name={isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter)}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      size="small"
                      checked={!!field.value}
                      onChange={(_, c) => field.onChange(c)}
                    />
                  }
                  label="선택안함"
                  sx={{
                    mr: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiSvgIcon-root': { fill: color },
                    '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                  }}
                />
              )}
            />
            <RHFMultiSelect
              name={isProjectPage ? 'bzStepCdList' : `${prefix}BzStepCdList`}
              label="단계"
              required={
                // 영업단계 선택안함 해제 시 한개이상 필수
                !watch(
                  isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
                )
              }
              disabled={
                !!watch(
                  isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
                )
              }
              // autoFocus={
              //   !watch(
              //     isProjectPage ? 'noBzStepCd' : (`no${capital}BzStepCd` as keyof IUnifiedFilter),
              //   )
              // }
              checkbox
              all
              options={codeData.BZ_STEP}
              size="small"
              sx={{ minWidth: 100, flex: 1 }}
            />
          </Stack>
        </FilterRow>

        {/* 확률 필터 */}
        <FilterRow label="수주확률">
          <Stack gap={0.5} direction="row" alignItems="center" flexWrap="wrap">
            <RHFSelect
              name={isProjectPage ? 'orderPrcnt' : `${prefix}Prcnt`}
              label="확률"
              size="small"
              sx={{ width: 90 }}
            >
              <MenuItem value={''}>선택안함</MenuItem>
              {['0', '10', '30', '50', '70', '90', '100'].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}%
                </MenuItem>
              ))}
            </RHFSelect>
            {watch(isProjectPage ? 'orderPrcnt' : (`${prefix}Prcnt` as keyof IUnifiedFilter)) && (
              <Controller
                name={isProjectPage ? 'orderSign' : (`${prefix}Sign` as keyof IUnifiedFilter)}
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    {codeData.SIGN.map((condition, i) => (
                      <FormControlLabel
                        key={`${condition.value}-${i}`}
                        defaultChecked
                        value={Number(condition.value)}
                        label={condition.label}
                        sx={{
                          mr: 0.5,
                          '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                        }}
                        control={
                          <Radio
                            size="small"
                            sx={{ py: 0.25 }}
                            color={section === 'sales' ? 'secondary' : 'primary'}
                          />
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            )}
          </Stack>
        </FilterRow>
      </Stack>
    );
  }
};
